import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, BookOpenCheck, CheckCircle2, Download, FileText,
  RefreshCw, ShieldCheck, Wrench,
} from "lucide-react";
import { toast } from "sonner";

import { AdminLayout } from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { adminApi, getApiErrorMessage } from "@/lib/api";

const statusTone = (status) => {
  if (["READY", "SAFE"].includes(status)) return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (status === "LIVE") return "border-red-300 bg-red-50 text-red-800";
  if (status === "BLOCKED") return "border-red-300 bg-red-50 text-red-800";
  return "border-amber-200 bg-amber-50 text-amber-900";
};

function StatusPill({ status }) {
  return <span className={`inline-flex shrink-0 rounded-full border px-2 py-1 text-[10px] font-bold tracking-wide ${statusTone(status)}`}>{status}</span>;
}

export default function OperationsGuide() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminApi.get("/admin/operations-guide");
      setData(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await adminApi.get("/admin/operations-guide/pdf", {
        responseType: "blob",
        timeout: 120_000,
      });
      const type = response.headers["content-type"] || "";
      if (!type.includes("application/pdf")) throw new Error("The server did not return a PDF document.");
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] || `SMIFS-Full-Scope-Operations-Guide-v${data?.guide?.version || "current"}.pdf`;
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Operations guide PDF downloaded with the current readiness snapshot");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setDownloading(false);
    }
  };

  const guide = data?.guide || {};
  const readiness = data?.readiness || {};
  const items = readiness.items || [];
  const actionCount = (readiness.counts?.ACTION || 0) + (readiness.counts?.CAUTION || 0);

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[var(--cta)]">
            <BookOpenCheck className="h-4 w-4" /> Controlled operating handbook
          </div>
          <h1 className="font-heading text-3xl text-[var(--brand)]">Full scope & operations guide</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-[var(--ink-muted)]">
            A beginner-safe, click-by-click handbook for setup, UAT, production, BSE transactions,
            RTA feeds, research, AIF/PMS, reconciliation, security, backup and incidents.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh readiness
          </Button>
          <Button onClick={downloadPdf} disabled={loading || downloading || !data}>
            <Download className={`mr-2 h-4 w-4 ${downloading ? "animate-bounce" : ""}`} />
            {downloading ? "Building PDF…" : "Download full PDF"}
          </Button>
        </div>
      </div>

      <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div><strong>The PDF is a controlled copy, not a substitute for current status.</strong> Every download is stamped with the current BSE environment and a non-secret live readiness snapshot. Always refresh this page before a production action.</div>
        </div>
      </div>

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {[
          ["Version", guide.version || "—", FileText],
          ["Detailed sections", guide.section_count ?? "—", BookOpenCheck],
          ["Words", guide.word_count?.toLocaleString() || "—", FileText],
          ["Estimated PDF pages", guide.estimated_pages || "—", FileText],
          ["Needs attention", actionCount, Wrench],
        ].map(([label, value, Icon]) => (
          <Card key={label} className="border-[var(--glass-border)]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between text-xs text-[var(--ink-muted)]"><span>{label}</span><Icon className="h-4 w-4" /></div>
              <div className="mt-2 font-heading text-2xl text-[var(--brand)]">{loading ? "…" : value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mb-6 border-[var(--glass-border)]">
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Live readiness</CardTitle>
              <p className="mt-1 text-xs text-[var(--ink-muted)]">
                {readiness.generated_at ? `Read from ${readiness.database} at ${new Date(readiness.generated_at).toLocaleString()}` : "Loading current application state…"}
              </p>
            </div>
            {readiness.environment && <StatusPill status={readiness.environment === "LIVE" ? "LIVE" : "CAUTION"} />}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.area} className="rounded-xl border border-[var(--glass-border)] p-3 sm:p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="font-semibold text-[var(--ink)]">{item.area}</div>
                  <StatusPill status={item.status} />
                </div>
                <div className="mt-2 text-sm leading-5 text-[var(--ink)]">{item.current}</div>
                <div className="mt-2 rounded-lg bg-[var(--surface-2)] p-2.5 text-xs leading-5 text-[var(--ink-muted)]">
                  <strong className="text-[var(--ink)]">Next:</strong> {item.next_step}
                </div>
              </div>
            ))}
            {!loading && !items.length && <div className="py-8 text-center text-sm text-[var(--ink-muted)]">Readiness data is unavailable. Refresh before continuing.</div>}
          </div>
        </CardContent>
      </Card>

      <Card className="border-[var(--glass-border)]">
        <CardHeader>
          <CardTitle>What the PDF teaches</CardTitle>
          <p className="text-sm text-[var(--ink-muted)]">Each section includes prerequisites, exact steps, expected evidence and stop conditions.</p>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {(guide.sections || []).map((section, index) => (
              <div key={section.id} className="rounded-xl border border-[var(--glass-border)] p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--surface-2)] text-xs font-bold text-[var(--brand)]">{index + 1}</div>
                  <div className="min-w-0">
                    <div className="font-semibold leading-5 text-[var(--ink)]">{section.title}</div>
                    <div className="mt-1 text-xs text-[var(--ink-muted)]">{section.subsections.length} topics · {section.steps} numbered steps</div>
                    {!!section.subsections.length && <div className="mt-2 line-clamp-3 text-xs leading-5 text-[var(--ink-muted)]">{section.subsections.slice(0, 4).join(" · ")}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 flex items-start gap-2 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
            <div>The guide never contains passwords, private keys or tokens. Its live appendix performs database/configuration checks without calling an external service or moving money.</div>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
